// components/notification/NotificationProvider.tsx
"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";

type Variant = "success" | "error" | "warning" | "info" | "default";

type Notif = {
  id: number;
  message: React.ReactNode;
  slot: number;
  duration: number;
  expireAt: number;
  variant: Variant;
};

type ShowOpts = {
  type?: Variant;
  duration?: number;
};

type NotificationCtx = {
  showNotification: (message: React.ReactNode, opts?: ShowOpts) => void;
};

const NotificationContext = createContext<NotificationCtx | undefined>(
  undefined
);
export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx)
    throw new Error("useNotification must be used within NotificationProvider");
  return ctx;
};

// 全局通知引用，供非组件代码（如 fetcher.ts）调用
type ShowNotificationFn = (message: string, opts?: ShowOpts) => void;
let globalShowNotification: ShowNotificationFn | null = null;

export const setGlobalNotification = (fn: ShowNotificationFn | null) => {
  globalShowNotification = fn;
};

export const showGlobalNotification: ShowNotificationFn = (message, opts) => {
  if (globalShowNotification) {
    globalShowNotification(message, opts);
  }
};

// 配置（按需调整）
const GAP = 72; // 每条通知占位高度（px）
const EXIT_MS = 300;

/////////////////////
// 内置图标（SVG） //
/////////////////////
const IconWrapper = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <span
    className={className}
    aria-hidden
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </span>
);

const SuccessIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <IconWrapper className={className}>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="currentColor"
        d="M9.00098 16.2L4.80098 12L3.40098 13.4L9.00098 19L21.00098 7.00002L19.60098 5.60002L9.00098 16.2Z"
      />
    </svg>
  </IconWrapper>
);

const ErrorIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <IconWrapper className={className}>
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
      />
    </svg>
  </IconWrapper>
);

const WarningIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <IconWrapper className={className}>
    <svg
      viewBox="0 0 24 24"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <path
        fill="currentColor"
        d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
      />
    </svg>
  </IconWrapper>
);

const InfoIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <IconWrapper className={className}>
    <svg
      viewBox="0 0 24 24"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <path
        fill="currentColor"
        d="M12 2a10 10 0 100 20 10 10 0 000-20zM11 6h2v2h-2V6zm0 4h2v6h-2v-6z"
      />
    </svg>
  </IconWrapper>
);

const DefaultIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <IconWrapper className={className}>
    <svg
      viewBox="0 0 24 24"
      className="w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <path
        fill="currentColor"
        d="M12 2a10 10 0 100 20 10 10 0 000-20zM11 6h2v2h-2V6zm0 4h2v6h-2v-6z"
      />
    </svg>
  </IconWrapper>
);

const variantToIcon = (v: Variant) => {
  switch (v) {
    case "success":
      return <SuccessIcon />;
    case "error":
      return <ErrorIcon />;
    case "warning":
      return <WarningIcon />;
    case "info":
      return <InfoIcon />;
    default:
      return <DefaultIcon />;
  }
};

// variant -> Tailwind color classes for icon/text
const variantColorClass = (v: Variant) => {
  switch (v) {
    case "success":
      return "text-green-600";
    case "error":
      return "text-red-600";
    case "warning":
      return "text-yellow-500";
    case "info":
      return "text-blue-600";
    default:
      return "text-gray-700";
  }
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifs, setNotifs] = useState<Notif[]>([]);
  const nextSlot = useRef(0);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const remaining = useRef<Map<number, number>>(new Map()); // pause 时剩余 ms
  const compressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerPaused = useRef(false);

  useEffect(() => {
    return () => {
      timers.current.forEach((t) => clearTimeout(t));
      if (compressTimer.current) clearTimeout(compressTimer.current);
    };
  }, []);

  const scheduleRemove = (id: number, ms: number) => {
    if (timers.current.has(id)) clearTimeout(timers.current.get(id)!);
    const t = setTimeout(() => removeNotification(id), ms);
    timers.current.set(id, t);
  };

  const showNotification = (message: React.ReactNode, opts?: ShowOpts) => {
    const duration = opts?.duration ?? 3000;
    const variant = opts?.type ?? "info";
    const id = Date.now() + Math.floor(Math.random() * 1000);
    const slot = nextSlot.current++;
    const now = Date.now();
    const n: Notif = {
      id,
      message,
      slot,
      duration,
      expireAt: now + duration,
      variant,
    };

    setNotifs((prev) => [...prev, n]);

    if (!containerPaused.current) scheduleRemove(id, duration);
    else remaining.current.set(id, duration);
  };

  // 将 showNotification 注册到全局引用
  useEffect(() => {
    setGlobalNotification(showNotification);
    return () => setGlobalNotification(null);
  });

  const removeNotification = (id: number) => {
    // 立刻从数组中删除（触发 AnimatePresence exit）
    setNotifs((prev) => prev.filter((p) => p.id !== id));

    if (timers.current.has(id)) {
      clearTimeout(timers.current.get(id)!);
      timers.current.delete(id);
    }
    remaining.current.delete(id);

    // compress after exit animation completes
    if (compressTimer.current) clearTimeout(compressTimer.current);
    compressTimer.current = setTimeout(() => {
      setNotifs((prev) => {
        const sorted = prev.slice().sort((a, b) => a.slot - b.slot);
        const reassigned = sorted.map((item, i) => ({ ...item, slot: i }));
        nextSlot.current = reassigned.length;
        return reassigned;
      });
      compressTimer.current = null;
    }, EXIT_MS + 10);
  };

  const pauseAll = () => {
    if (containerPaused.current) return;
    containerPaused.current = true;
    const now = Date.now();
    // clear timers and record remaining
    timers.current.forEach((t, id) => {
      clearTimeout(t);
      timers.current.delete(id);
      const found = notifs.find((n) => n.id === id);
      if (found) {
        const rem = Math.max(0, found.expireAt - now);
        remaining.current.set(id, rem);
      }
    });
  };

  const resumeAll = () => {
    if (!containerPaused.current) return;
    containerPaused.current = false;
    const now = Date.now();
    setNotifs((prev) => {
      prev.forEach((n) => {
        const rem = remaining.current.get(n.id);
        const ms = rem ?? Math.max(0, n.expireAt - now);
        n.expireAt = now + ms;
        scheduleRemove(n.id, ms);
        remaining.current.delete(n.id);
      });
      return prev;
    });
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}

      {/* container */}
      <div
        className="fixed top-4 left-0 right-0 z-50 flex flex-col items-center pointer-events-none"
        onMouseEnter={pauseAll}
        onMouseLeave={resumeAll}
      >
        <AnimatePresence>
          {notifs.map((n) => {
            const topPx = n.slot * GAP;
            return (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{
                  default: { duration: 0.22 },
                }}
                style={{ marginTop: n.slot === 0 ? 0 : GAP }}
                className="pointer-events-auto"
              >
                <div className="inline-flex items-center gap-3 px-4 py-3 rounded-lg bg-white shadow text-black whitespace-nowrap overflow-hidden">
                  <div
                    className={`flex-shrink-0 w-6 h-6 ${variantColorClass(
                      n.variant
                    )}`}
                  >
                    <span className="inline-flex w-full h-full items-center justify-center">
                      {variantToIcon(n.variant)}
                    </span>
                  </div>

                  <div className="text-sm break-words whitespace-normal">
                    {n.message}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};
